/**
 * Cookie consent banner (FR-P-1, FR-P-2, B9, D24).
 *
 * First-load banner with three categories. PostHog and Sentry session replay
 * are gated until the user accepts (analytics=true). Necessary cookies (auth
 * session, CSRF) load unconditionally — `necessary` is locked to true and
 * cannot be opted out of (enforced server-side too in
 * `caitapi/routers/cookie_consent.py`).
 *
 * Accessibility:
 * - Banner uses role="dialog" + aria-modal="true" so screen readers
 *   announce it as a modal interrupting the page flow.
 * - Focus is trapped to the banner buttons until a choice is made.
 * - Each category has a labeled checkbox; "necessary" is shown disabled+
 *   checked so the user understands it's required.
 */

import * as React from "react";
import { useEffect, useId, useRef, useState } from "react";

import { cn } from "@/lib/utils";
import {
  consentIsStale,
  recordConsent,
  readConsentFromCookie,
  type ConsentChoice,
} from "@/lib/cookieConsent";

interface CookieConsentBannerProps {
  /** Force the banner to render regardless of stored consent. Useful for
   *  the "Manage cookie preferences" link in the footer (Day 4+). */
  forceShow?: boolean;
  /** Called after a choice is recorded so the parent can re-init analytics. */
  onChoice?: (choice: ConsentChoice) => void;
}

export function CookieConsentBanner({
  forceShow = false,
  onChoice,
}: CookieConsentBannerProps): React.JSX.Element | null {
  const [visible, setVisible] = useState<boolean>(false);
  const [showCustomize, setShowCustomize] = useState<boolean>(false);
  const [analytics, setAnalytics] = useState<boolean>(false);
  const [marketing, setMarketing] = useState<boolean>(false);
  const [submitting, setSubmitting] = useState<boolean>(false);
  const [error, setError] = useState<string | null>(null);

  const acceptAllButtonRef = useRef<HTMLButtonElement>(null);
  const headingId = useId();
  const descId = useId();

  // Decide whether to show on mount. Existing valid consent → hidden.
  useEffect(() => {
    if (forceShow) {
      setVisible(true);
      return;
    }
    setVisible(consentIsStale());
  }, [forceShow]);

  // Hydrate prior choices when re-prompting on a stale consent.
  useEffect(() => {
    if (!visible) return;
    const prior = readConsentFromCookie();
    if (prior) {
      setAnalytics(prior.analytics);
      setMarketing(prior.marketing);
    }
  }, [visible]);

  // Move keyboard focus into the banner so screen readers + keyboard users
  // see it without searching.
  useEffect(() => {
    if (visible) {
      acceptAllButtonRef.current?.focus();
    }
  }, [visible]);

  if (!visible) return null;

  async function submit(choice: ConsentChoice): Promise<void> {
    setSubmitting(true);
    setError(null);
    try {
      await recordConsent(choice);
      setVisible(false);
      onChoice?.(choice);
    } catch (err) {
      setError(
        err instanceof Error
          ? err.message
          : "Failed to record consent. Please try again.",
      );
    } finally {
      setSubmitting(false);
    }
  }

  return (
    <div
      className="fixed inset-x-0 bottom-0 z-50 px-4 pb-4 sm:px-6 sm:pb-6"
      role="dialog"
      aria-modal="true"
      aria-labelledby={headingId}
      aria-describedby={descId}
    >
      <div
        className={cn(
          "mx-auto max-w-3xl rounded-xl border border-border bg-card/95 backdrop-blur shadow-card",
          "ring-1 ring-foreground/5",
        )}
      >
        <div className="p-5 sm:p-6">
          <h2
            id={headingId}
            className="text-base font-semibold text-foreground"
          >
            Your privacy on CaiT Academy
          </h2>
          <p id={descId} className="mt-2 text-sm text-muted-foreground">
            We use cookies to keep you signed in and to understand how the
            course is used. You can accept all, decline non-essential, or
            customize. Necessary cookies (your session, CSRF) load
            unconditionally so the site works. See our{" "}
            <a
              href="/privacy"
              className="font-medium text-primary underline underline-offset-2 hover:text-primary/80"
            >
              Privacy Policy
            </a>{" "}
            for details.
          </p>

          {showCustomize ? (
            <fieldset className="mt-4 space-y-2 border-t border-border pt-4">
              <legend className="sr-only">Cookie categories</legend>
              <CategoryCheckbox
                id="cookie-necessary"
                label="Necessary"
                description="Session, CSRF, and other cookies required for the site to function. Always on."
                checked
                disabled
                onChange={() => {
                  /* locked */
                }}
              />
              <CategoryCheckbox
                id="cookie-analytics"
                label="Analytics"
                description="PostHog product analytics + Sentry session replay (with PII redacted). Helps us improve lessons."
                checked={analytics}
                disabled={submitting}
                onChange={setAnalytics}
              />
              <CategoryCheckbox
                id="cookie-marketing"
                label="Marketing"
                description="Reserved. We do not use marketing cookies in Phase 0."
                checked={marketing}
                disabled={submitting}
                onChange={setMarketing}
              />
            </fieldset>
          ) : null}

          {error ? (
            <p
              role="alert"
              className="mt-3 text-sm text-destructive"
            >
              {error}
            </p>
          ) : null}

          <div className="mt-5 flex flex-col gap-2 sm:flex-row sm:justify-end">
            <button
              type="button"
              className={cn(
                "inline-flex items-center justify-center rounded-md border border-border bg-background px-4 py-2",
                "text-sm font-medium text-foreground shadow-sm",
                "hover:bg-muted focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary",
                "disabled:cursor-not-allowed disabled:opacity-50 transition-colors",
              )}
              disabled={submitting}
              onClick={() => {
                void submit({ analytics: false, marketing: false });
              }}
            >
              Decline non-essential
            </button>

            {showCustomize ? (
              <button
                type="button"
                className={cn(
                  "inline-flex items-center justify-center rounded-md border border-border bg-background px-4 py-2",
                  "text-sm font-medium text-foreground shadow-sm",
                  "hover:bg-muted focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary",
                  "disabled:cursor-not-allowed disabled:opacity-50 transition-colors",
                )}
                disabled={submitting}
                onClick={() => {
                  void submit({ analytics, marketing });
                }}
              >
                Save preferences
              </button>
            ) : (
              <button
                type="button"
                className={cn(
                  "inline-flex items-center justify-center rounded-md border border-border bg-background px-4 py-2",
                  "text-sm font-medium text-foreground shadow-sm",
                  "hover:bg-muted focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary",
                  "transition-colors",
                )}
                onClick={() => setShowCustomize(true)}
              >
                Customize
              </button>
            )}

            <button
              ref={acceptAllButtonRef}
              type="button"
              className={cn(
                "inline-flex items-center justify-center rounded-md border border-transparent bg-primary px-4 py-2",
                "text-sm font-semibold text-primary-foreground shadow-sm",
                "hover:bg-primary/90 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary focus-visible:ring-offset-2",
                "disabled:cursor-not-allowed disabled:opacity-50 transition-colors",
              )}
              disabled={submitting}
              onClick={() => {
                void submit({ analytics: true, marketing: true });
              }}
            >
              Accept all
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}

interface CategoryCheckboxProps {
  id: string;
  label: string;
  description: string;
  checked: boolean;
  disabled?: boolean;
  onChange: (next: boolean) => void;
}

function CategoryCheckbox({
  id,
  label,
  description,
  checked,
  disabled,
  onChange,
}: CategoryCheckboxProps): React.JSX.Element {
  return (
    <div className="flex items-start gap-3">
      <input
        id={id}
        type="checkbox"
        checked={checked}
        disabled={disabled}
        onChange={(e) => onChange(e.target.checked)}
        className={cn(
          "mt-0.5 h-4 w-4 rounded border-border text-primary accent-primary",
          "focus-visible:ring-2 focus-visible:ring-primary",
          "disabled:cursor-not-allowed disabled:opacity-60",
        )}
      />
      <div className="flex-1">
        <label
          htmlFor={id}
          className="block text-sm font-medium text-foreground"
        >
          {label}
          {disabled ? (
            <span className="ml-2 text-xs font-normal uppercase tracking-[0.12em] text-muted-foreground">
              Required
            </span>
          ) : null}
        </label>
        <p className="text-xs text-muted-foreground">{description}</p>
      </div>
    </div>
  );
}
